import { type ReactNode, type RefObject, useLayoutEffect, useState } from 'react';

/** En-dessous, l'écran est traité comme un smartphone : feuille ancrée, pas boîte centrée. */
const MOBILE_BREAKPOINT_PX = 640;

interface Props {
  /**
   * Élément dont le haut fixe la limite de couverture : la feuille recouvre
   * tout depuis là jusqu'en bas, quel que soit le téléphone. Une fraction fixe
   * de l'écran ne conviendrait pas à tous les appareils — la mesure réelle, si.
   *
   * Omis, la feuille couvre l'écran entier : c'est ce qu'on veut d'un
   * formulaire de création, qui n'a rien à laisser voir derrière lui.
   */
  anchorRef?: RefObject<HTMLElement | null>;
  label: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Feuille de bas d'écran, éventuellement ancrée sur un élément de la page.
 *
 * Sur mobile, elle couvre tout depuis le haut de l'élément d'ancrage jusqu'en
 * bas ; ce qui est au-dessus reste visible et intact. Sur grand écran, elle
 * redevient une boîte centrée sur fond assombri, sans dépendre d'une mesure.
 */
export function BottomSheet({ anchorRef, label, onClose, children }: Props) {
  // Nul sur grand écran, ou sans ancre : les classes `sm:` et `inset-0`
  // prennent alors le relais.
  const [anchorTopPx, setAnchorTopPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    function measure() {
      if (anchorRef === undefined || window.innerWidth >= MOBILE_BREAKPOINT_PX) {
        setAnchorTopPx(null);
        return;
      }
      setAnchorTopPx(anchorRef.current?.getBoundingClientRect().top ?? null);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [anchorRef]);

  return (
    // Le panneau remplit toute la zone couverte : il n'y a donc pas de fond à
    // cliquer sur mobile, seul le bouton de fermeture le fait.
    <div
      className="fixed inset-0 z-50 sm:flex sm:items-center sm:justify-center sm:bg-black/40"
      style={anchorTopPx === null ? undefined : { top: `${anchorTopPx}px` }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="safe-bottom h-full w-full overflow-y-auto rounded-t-2xl bg-surface p-4 shadow-2xl sm:h-auto sm:max-h-[80vh] sm:max-w-md sm:rounded-2xl sm:shadow-none"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{label}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-lg px-2 py-1 text-ink-muted active:bg-line"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
